<template>
  <div class="image-viewer-wrapper" :class="containerClasses">
    <!-- Loading state with skeleton -->
    <div v-if="isLoading" class="image-container" :class="aspectClass">
      <div class="image-sizer" :class="skeletonClasses">
        <div class="skeleton-loader"></div>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="hasError" class="error-container text-center">
      <div class="error-icon mb-3">
        <i class="bi bi-image-fill text-muted" style="font-size: 3rem"></i>
      </div>
      <p class="error-message text-muted mb-3">Unable to load image</p>
      <button v-if="imageUrl" @click="openInNewTab" class="btn btn-sm btn-outline-primary">
        <i class="bi bi-box-arrow-up-right me-2"></i>
        Open original
      </button>
    </div>

    <!-- Image display -->
    <div v-else class="image-container" :class="aspectClass">
      <div class="image-sizer">
        <img
          :src="imageUrl"
          :alt="altText"
          :class="imageClasses"
          @load="handleImageLoad"
          @error="handleImageError"
        />
      </div>

      <!-- Image controls (if enabled) -->
      <div v-if="showControls && !hasError" class="image-controls">
        <button @click="openInNewTab" class="btn btn-sm btn-light" title="Open in new tab">
          <i class="bi bi-box-arrow-up-right"></i>
        </button>
        <button
          v-if="imageUrl"
          @click="downloadImage"
          class="btn btn-sm btn-light"
          title="Download"
        >
          <i class="bi bi-download"></i>
        </button>
        <button
          v-if="canToggleFit"
          @click="toggleObjectFit"
          class="btn btn-sm btn-light"
          title="Toggle fit"
        >
          <i class="bi bi-arrows-angle-expand"></i>
        </button>
      </div>
    </div>

    <!-- Hidden preload image -->
    <img
      v-if="isLoading && imageUrl"
      :src="imageUrl"
      @load="handlePreloadComplete"
      @error="handleImageError"
      style="position: absolute; visibility: hidden; pointer-events: none"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { reportError } from '@lib/errorHandler'
import {
  getAspectRatioBucket,
  getSizeClass,
  getImageClasses,
  preloadImage,
  type ImageDimensions,
  type ViewContext,
  type AspectRatioBucket,
  type SizeClass,
} from '@lib/imageScaling'

interface FileAsset {
  file_url?: string
  remote_file_url?: string
  title?: string
  description?: string
  [key: string]: unknown
}

export default defineComponent({
  name: 'ImageViewer',

  props: {
    fileAsset: {
      type: Object as PropType<FileAsset>,
      required: false,
      default: null,
    },
    fileObject: {
      type: File as PropType<File>,
      required: false,
      default: null,
    },
    context: {
      type: String as PropType<ViewContext>,
      default: 'embedded',
    },
    showControls: {
      type: Boolean,
      default: false,
    },
    enableProgressiveLoading: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isLoading: true,
      hasError: false,
      blobUrl: null as string | null,
      imageDimensions: null as ImageDimensions | null,
      aspectRatioBucket: 'square' as AspectRatioBucket,
      sizeClass: 'medium' as SizeClass,
      userPreferCover: false,
    }
  },

  computed: {
    imageUrl(): string | null {
      // Handle file object (for previewing uploads)
      if (this.fileObject) {
        return this.blobUrl
      }

      // Handle file asset from API
      if (this.fileAsset) {
        // Use remote_file_url if available (external URLs)
        if (this.fileAsset.remote_file_url) {
          return this.fileAsset.remote_file_url
        }

        // Use file_url with base URL for internal files
        if (this.fileAsset.file_url) {
          const baseUrl = process.env.VUE_APP_UE_APP_URL || ''
          return `${baseUrl}${this.fileAsset.file_url}`
        }
      }

      return null
    },

    altText(): string {
      if (this.fileAsset?.title) {
        return this.fileAsset.title
      }
      if (this.fileObject?.name) {
        return this.fileObject.name
      }
      return 'Image'
    },

    containerClasses(): string[] {
      return [
        `context-${this.context}`,
        this.isLoading ? 'is-loading' : '',
        this.hasError ? 'has-error' : '',
      ].filter(Boolean)
    },

    aspectClass(): string {
      return `aspect-${this.aspectRatioBucket}`
    },

    imageClasses(): string[] {
      const classes = getImageClasses(
        this.imageDimensions,
        this.context,
        this.aspectRatioBucket,
        this.sizeClass,
      )

      if (this.userPreferCover) {
        classes.push('user-prefer-cover')
      }

      return classes
    },

    skeletonClasses(): string[] {
      return ['skeleton-container']
    },

    canToggleFit(): boolean {
      return this.context === 'modal' || this.context === 'fullscreen'
    },
  },

  async mounted() {
    // Create blob URL if we have a file object
    if (this.fileObject) {
      try {
        this.blobUrl = URL.createObjectURL(this.fileObject)
      } catch (error) {
        this.handleImageError()
        reportError(error as Error, 'createObjectURL', 'ImageViewer')
      }
    }

    // If we don't have an image URL, show error immediately
    if (!this.imageUrl) {
      this.isLoading = false
      this.hasError = true
      return
    }

    // Preload image to get dimensions for better layout
    if (this.enableProgressiveLoading) {
      try {
        await this.preloadImageDimensions()
      } catch (error) {
        // Preload failed, but we'll still try to show the image
        reportError(error as Error, 'preloadImageDimensions', 'ImageViewer')
      }
    }
  },

  beforeDestroy() {
    // Clean up blob URL to prevent memory leaks
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl)
    }
  },

  methods: {
    async preloadImageDimensions() {
      if (!this.imageUrl) return

      try {
        const dimensions = await preloadImage(this.imageUrl)
        this.imageDimensions = dimensions
        this.aspectRatioBucket = getAspectRatioBucket(dimensions.width, dimensions.height)
        this.sizeClass = getSizeClass(dimensions.width, dimensions.height)

        // Emit dimensions for parent components
        this.$emit('dimensions-loaded', dimensions)
      } catch (error) {
        throw new Error(`Failed to preload image dimensions: ${error}`)
      }
    },

    handlePreloadComplete() {
      // Called when hidden preload image loads
      this.isLoading = false
      this.hasError = false
    },

    handleImageLoad(event: Event) {
      const img = event.target as HTMLImageElement

      // Capture dimensions if not already available
      if (!this.imageDimensions && img.naturalWidth && img.naturalHeight) {
        this.imageDimensions = {
          width: img.naturalWidth,
          height: img.naturalHeight,
        }
        this.aspectRatioBucket = getAspectRatioBucket(img.naturalWidth, img.naturalHeight)
        this.sizeClass = getSizeClass(img.naturalWidth, img.naturalHeight)

        this.$emit('dimensions-loaded', this.imageDimensions)
      }

      this.isLoading = false
      this.hasError = false
    },

    handleImageError() {
      this.isLoading = false
      this.hasError = true

      // Don't report error for expected cases (no URL)
      if (this.imageUrl) {
        const error = new Error(`Failed to load image: ${this.imageUrl}`)
        reportError(error, 'handleImageError', 'ImageViewer')
      }

      this.$emit('load-error')
    },

    toggleObjectFit() {
      this.userPreferCover = !this.userPreferCover
    },

    openInNewTab() {
      if (this.imageUrl) {
        window.open(this.imageUrl, '_blank', 'noopener,noreferrer')
      }
    },

    downloadImage() {
      if (!this.imageUrl) {
        this.$toast.error('Unable to download image')
        return
      }

      const link = document.createElement('a')
      link.href = this.imageUrl
      link.download = this.altText || 'image'
      link.style.display = 'none'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
})
</script>

<style lang="sass" scoped>
// CSS Custom Properties for dynamic sizing
.image-viewer-wrapper
  --max-width: 100%
  --max-height: 600px
  --min-size: 400px

  display: flex
  justify-content: center
  align-items: center
  width: 100%
  min-height: var(--min-size)
  position: relative

  // Context-specific sizing
  &.context-library
    --max-width: 400px
    --max-height: 300px
    --min-size: 150px

  &.context-modal
    --max-width: min(90vw, 1200px)
    --max-height: 70vh
    --min-size: 200px

  &.context-fullscreen
    --max-width: 95vw
    --max-height: 95vh
    --min-size: 200px

  &.context-embedded
    --max-width: 600px
    --max-height: 400px
    --min-size: 150px

  // Error state
  .error-container
    background-color: var(--ue-gray-container-bg)
    border-radius: 0.5rem
    max-width: 400px
    width: 100%
    padding: 2rem

    .error-message
      color: var(--ue-gray-container-color)

// Image container with aspect ratio preservation
.image-container
  position: relative
  width: 100%
  max-width: var(--max-width)
  display: flex
  align-items: center
  justify-content: center

  // Ensure container doesn't exceed parent bounds while allowing centering
  &.aspect-landscape, &.aspect-panoramic, &.aspect-ultra-wide
    max-width: min(var(--max-width), 100%)
    margin: 0 auto

  // Aspect ratio classes using padding-top technique
  &.aspect-ultra-tall
    max-width: 400px
    min-width: 300px
    .image-sizer
      padding-top: 250% // 2:5 ratio (ultra-tall)

  &.aspect-portrait
    max-width: 550px
    min-width: 400px
    .image-sizer
      padding-top: 133.33% // 3:4 ratio (portrait)

  &.aspect-square
    max-width: 600px
    min-width: 400px
    .image-sizer
      padding-top: 100% // 1:1 ratio (square)

  &.aspect-landscape
    min-width: 500px
    .image-sizer
      padding-top: 56.25% // 16:9 ratio (landscape)

  &.aspect-panoramic
    min-width: 600px
    .image-sizer
      padding-top: 40% // 2.5:1 ratio (panoramic)

  &.aspect-ultra-wide
    min-width: 600px
    .image-sizer
      padding-top: 25% // 4:1 ratio (ultra-wide)
      max-height: 300px

// Image sizer with object-fit control
.image-sizer
  position: relative
  width: 100%
  overflow: hidden
  border-radius: 0.25rem
  max-height: var(--max-height)

  // Only show background for skeleton loading state
  &.skeleton-container
    background: var(--ue-gray-container-bg)

  img
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    transition: object-fit 0.2s ease
    // Override any global CSS that might set max-height on images
    // This is more specific than .image-container img
    max-height: 100% !important

    // Object-fit strategies
    &.object-fit-contain
      object-fit: contain
      object-position: center

    &.object-fit-cover
      object-fit: cover
      object-position: center

    &.object-fit-none
      object-fit: none
      width: auto
      height: auto
      max-width: 100%
      max-height: 100%
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)
      position: absolute

    // User preference override
    &.user-prefer-cover
      object-fit: cover !important

    // Size-specific adjustments
    &.size-tiny
      // Tiny images get special treatment
      object-fit: none
      width: auto
      height: auto
      max-width: 100%
      max-height: 100%
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)

// Skeleton loader
.skeleton-container
  .skeleton-loader
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    background: linear-gradient(90deg, var(--ue-gray-container-bg) 0%, #f0f0f0 50%, var(--ue-gray-container-bg) 100%)
    background-size: 200% 100%
    animation: shimmer 1.5s ease-in-out infinite
    border-radius: 0.25rem

@keyframes shimmer
  0%
    background-position: -200% 0
  100%
    background-position: 200% 0

// Image controls
.image-controls
  position: absolute
  top: 0.5rem
  right: 0.5rem
  display: flex
  gap: 0.25rem
  opacity: 0
  transition: opacity 0.2s ease

  .btn
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)

.image-container:hover .image-controls
  opacity: 1

// Responsive adjustments
@media (max-width: 768px)
  .image-viewer-wrapper
    &.context-library
      --max-width: 100%
      --max-height: 40vh

    &.context-modal
      --max-width: 95vw
      --max-height: 60vh

@media (max-width: 480px)
  .image-viewer-wrapper
    --min-size: 120px

    &.context-library
      --max-height: 30vh

// High DPI display adjustments
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)
  .image-sizer img
    image-rendering: -webkit-optimize-contrast
    image-rendering: crisp-edges

// Accessibility improvements
@media (prefers-reduced-motion: reduce)
  .skeleton-loader
    animation: none
    background: var(--ue-gray-container-bg)

  .image-sizer img
    transition: none

// Print styles
@media print
  .image-controls
    display: none

  .skeleton-loader
    display: none
</style>
